/**
 * Widget API Client
 *
 * Simple fetch wrapper for widget authentication with JWT token injection.
 * Handles authentication and error responses for widget customer portal.
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

interface RequestConfig {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

export class WidgetApiClient {
  private baseUrl: string;
  private getToken: () => string | null;

  constructor(getToken: () => string | null, baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
    this.getToken = getToken;
  }

  private async request<T>(
    endpoint: string,
    config: RequestConfig = {}
  ): Promise<T> {
    const { method = 'GET', body, headers = {} } = config;

    // Build headers
    const requestHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...headers,
    };

    // Add authorization header if token exists
    const token = this.getToken();
    if (token) {
      requestHeaders['Authorization'] = `Bearer ${token}`;
    }

    // Build request options
    const options: RequestInit = {
      method,
      headers: requestHeaders,
    };

    if (body && method !== 'GET') {
      options.body = JSON.stringify(body);
    }

    // Make request
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, options);

    // Handle non-2xx responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        error: 'An error occurred',
      }));

      throw new Error(
        errorData.error || errorData.message || `HTTP ${response.status}`
      );
    }

    // Parse and return response
    return response.json();
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', body });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', body });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}
